import {
  getObject,
  getList,
  getOptionalObject,
} from "@surya-digital/leo-ts-types";
import { SampleBreakdown, SampleBreakdownDTO } from "./sampleBreakdown";
import { SingleSelectResultV1, SingleSelectResultV1DTO } from "./singleSelectResultV1";
import { SingleSelectGraphTypeV1 } from "./singleSelectGraphTypeV1";
import { ProportionChartType } from "./proportionChartType";
import { SingleSelectWithProportionGraphTypeV1 } from "./singleSelectWithProportionGraphTypeV1";
import { Code, CodeDTO } from "../types/code";
import { SingleSelectNpsResultV1, SingleSelectNpsResultV1DTO } from "./singleSelectNpsResultV1";
import { SingleSelectNpsGraphTypeV1 } from "./singleSelectNpsGraphTypeV1";
import { MultiSelectResultV1, MultiSelectResultV1DTO } from "./multiSelectResultV1";
import { MultiSelectGraphTypeV1 } from "./multiSelectGraphTypeV1";
import { RankingResultV1, RankingResultV1DTO } from "./rankingResultV1";
import { RankingGraphTypeV1 } from "./rankingGraphTypeV1";
import { OpenEndedFieldResultV1, OpenEndedFieldResultV1DTO } from "./openEndedFieldResultV1";
import { OpenEndedGraphTypeV1 } from "./openEndedGraphTypeV1";
import { GroupElementResultV1, GroupElementResultV1DTO, GroupElementResultV1Enums } from "./groupElementResultV1";
import { GridResultV1, GridResultV1DTO, GridResultV1Enums } from "./gridResultV1";


export namespace QuestionViewTypeV1 {
  export interface SingleSelectDTO {
    case: string;
    sampleBreakdown: SampleBreakdownDTO;
    singleSelectResults: SingleSelectResultV1DTO[];
    singleSelectGraphType: SingleSelectGraphTypeV1.SingleSelectGraphTypeV1DTO;
  }
  
  export class SingleSelect {
    readonly isQuestionViewTypeV1SingleSelect: boolean = true;
  
    readonly sampleBreakdown: SampleBreakdown;
    readonly singleSelectResults: SingleSelectResultV1[];
    readonly singleSelectGraphType: SingleSelectGraphTypeV1.SingleSelectGraphTypeV1;
  
  
    constructor(
    sampleBreakdown: SampleBreakdown, 
    singleSelectResults: SingleSelectResultV1[], 
    singleSelectGraphType: SingleSelectGraphTypeV1.SingleSelectGraphTypeV1
    ) {
      this.sampleBreakdown = sampleBreakdown;
      this.singleSelectResults = singleSelectResults;
      this.singleSelectGraphType = singleSelectGraphType;
    }
  
    static fromDTO(dto: SingleSelectDTO): SingleSelect {
      const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidQuestionViewTypeV1SingleSelectSampleBreakdownError));
      const singleSelectResults = getList<SingleSelectResultV1DTO, SingleSelectResultV1>(
        dto,
        "singleSelectResults",
        SingleSelectResultV1.fromDTO,
        InvalidQuestionViewTypeV1SingleSelectSingleSelectResultsError
      );
      const singleSelectGraphType = SingleSelectGraphTypeV1.fromDTO(getObject(dto, "singleSelectGraphType", InvalidQuestionViewTypeV1SingleSelectSingleSelectGraphTypeError));
      return new SingleSelect(
        sampleBreakdown,
        singleSelectResults,
        singleSelectGraphType
      );
    }
  
    toDTO(): SingleSelectDTO {
      const dto: SingleSelectDTO = {
        case: "SINGLE_SELECT",
          sampleBreakdown: this.sampleBreakdown.toDTO(),
          singleSelectResults: this.singleSelectResults.map((e: SingleSelectResultV1) => { return e.toDTO() }),
          singleSelectGraphType: SingleSelectGraphTypeV1.toDTO(this.singleSelectGraphType)
      };
      return dto;
    }
    }
  
  SingleSelect.prototype.toString = function toString(): string {
    return `QuestionViewTypeV1.SINGLE_SELECT(sampleBreakdown='${this.sampleBreakdown}',singleSelectResults='${this.singleSelectResults}',singleSelectGraphType='${this.singleSelectGraphType}')`;
  };
  
  export interface SingleSelectWithProportionDTO {
    case: string;
    sampleBreakdown: SampleBreakdownDTO;
    singleSelectResults: SingleSelectResultV1DTO[];
    proportionChartType: ProportionChartType.ProportionChartTypeDTO;
    singleSelectWithProportionGraphType: SingleSelectWithProportionGraphTypeV1.SingleSelectWithProportionGraphTypeV1DTO;
    naOptionCode?: CodeDTO | null;
  }
  
  export class SingleSelectWithProportion {
    readonly isQuestionViewTypeV1SingleSelectWithProportion: boolean = true;
  
    readonly sampleBreakdown: SampleBreakdown;
    readonly singleSelectResults: SingleSelectResultV1[];
    readonly proportionChartType: ProportionChartType.ProportionChartType;
    readonly singleSelectWithProportionGraphType: SingleSelectWithProportionGraphTypeV1.SingleSelectWithProportionGraphTypeV1;
    readonly naOptionCode: Code | null;
  
  
    constructor(
    sampleBreakdown: SampleBreakdown, 
    singleSelectResults: SingleSelectResultV1[], 
    proportionChartType: ProportionChartType.ProportionChartType, 
    singleSelectWithProportionGraphType: SingleSelectWithProportionGraphTypeV1.SingleSelectWithProportionGraphTypeV1, 
    naOptionCode: Code | null = null
    ) {
      this.sampleBreakdown = sampleBreakdown;
      this.singleSelectResults = singleSelectResults;
      this.proportionChartType = proportionChartType;
      this.singleSelectWithProportionGraphType = singleSelectWithProportionGraphType;
      this.naOptionCode = naOptionCode;
    }
  
    static fromDTO(dto: SingleSelectWithProportionDTO): SingleSelectWithProportion {
      const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidQuestionViewTypeV1SingleSelectSampleBreakdownError));
      const singleSelectResults = getList<SingleSelectResultV1DTO, SingleSelectResultV1>(
        dto,
        "singleSelectResults",
        SingleSelectResultV1.fromDTO,
        InvalidQuestionViewTypeV1SingleSelectSingleSelectResultsError
      );
      const proportionChartType = ProportionChartType.fromDTO(getObject(dto, "proportionChartType", InvalidQuestionViewTypeV1SingleSelectWithProportionProportionChartTypeError));
      const singleSelectWithProportionGraphType = SingleSelectWithProportionGraphTypeV1.fromDTO(getObject(dto, "singleSelectWithProportionGraphType", InvalidQuestionViewTypeV1SingleSelectWithProportionSingleSelectWithProportionGraphTypeError));
      let naOptionCode = getOptionalObject(dto, "naOptionCode");
      if(naOptionCode !== undefined && naOptionCode !== null) {
        naOptionCode = Code.fromDTO(getObject(dto, "naOptionCode", InvalidQuestionViewTypeV1SingleSelectWithProportionNaOptionCodeError));
      }
      return new SingleSelectWithProportion(
        sampleBreakdown,
        singleSelectResults,
        proportionChartType,
        singleSelectWithProportionGraphType,
        naOptionCode
      );
    }
  
    toDTO(): SingleSelectWithProportionDTO {
      const dto: SingleSelectWithProportionDTO = {
        case: "SINGLE_SELECT_WITH_PROPORTION",
          sampleBreakdown: this.sampleBreakdown.toDTO(),
          singleSelectResults: this.singleSelectResults.map((e: SingleSelectResultV1) => { return e.toDTO() }),
          proportionChartType: this.proportionChartType.toDTO(),
          singleSelectWithProportionGraphType: SingleSelectWithProportionGraphTypeV1.toDTO(this.singleSelectWithProportionGraphType),
      };
      if (this.naOptionCode !== undefined && this.naOptionCode !== null){
        dto.naOptionCode = this.naOptionCode.toDTO();
      }
      return dto;
    }
    }
  
  SingleSelectWithProportion.prototype.toString = function toString(): string {
    return `QuestionViewTypeV1.SINGLE_SELECT_WITH_PROPORTION(sampleBreakdown='${this.sampleBreakdown}',singleSelectResults='${this.singleSelectResults}',proportionChartType='${this.proportionChartType}',singleSelectWithProportionGraphType='${this.singleSelectWithProportionGraphType}',naOptionCode='${this.naOptionCode}')`;
  };
  
  export interface SingleSelectNpsDTO {
    case: string;
    sampleBreakdown: SampleBreakdownDTO;
    promoters: SingleSelectNpsResultV1DTO;
    passives: SingleSelectNpsResultV1DTO;
    detractors: SingleSelectNpsResultV1DTO;
    singleSelectNpsResultsBreakdownV1: SingleSelectResultV1DTO[];
    singleSelectNpsGraphType: SingleSelectNpsGraphTypeV1.SingleSelectNpsGraphTypeV1DTO;
  }
  
  export class SingleSelectNps {
    readonly isQuestionViewTypeV1SingleSelectNps: boolean = true;
  
    readonly sampleBreakdown: SampleBreakdown;
    readonly promoters: SingleSelectNpsResultV1;
    readonly passives: SingleSelectNpsResultV1;
    readonly detractors: SingleSelectNpsResultV1;
    readonly singleSelectNpsResultsBreakdownV1: SingleSelectResultV1[];
    readonly singleSelectNpsGraphType: SingleSelectNpsGraphTypeV1.SingleSelectNpsGraphTypeV1;
  
  
    constructor(
    sampleBreakdown: SampleBreakdown, 
    promoters: SingleSelectNpsResultV1, 
    passives: SingleSelectNpsResultV1, 
    detractors: SingleSelectNpsResultV1, 
    singleSelectNpsResultsBreakdownV1: SingleSelectResultV1[], 
    singleSelectNpsGraphType: SingleSelectNpsGraphTypeV1.SingleSelectNpsGraphTypeV1
    ) {
      this.sampleBreakdown = sampleBreakdown;
      this.promoters = promoters;
      this.passives = passives;
      this.detractors = detractors;
      this.singleSelectNpsResultsBreakdownV1 = singleSelectNpsResultsBreakdownV1;
      this.singleSelectNpsGraphType = singleSelectNpsGraphType;
    }
  
    static fromDTO(dto: SingleSelectNpsDTO): SingleSelectNps {
      const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidQuestionViewTypeV1SingleSelectSampleBreakdownError));
      const promoters = SingleSelectNpsResultV1.fromDTO(getObject(dto, "promoters", InvalidQuestionViewTypeV1SingleSelectNpsPromotersError));
      const passives = SingleSelectNpsResultV1.fromDTO(getObject(dto, "passives", InvalidQuestionViewTypeV1SingleSelectNpsPassivesError));
      const detractors = SingleSelectNpsResultV1.fromDTO(getObject(dto, "detractors", InvalidQuestionViewTypeV1SingleSelectNpsDetractorsError));
      const singleSelectNpsResultsBreakdownV1 = getList<SingleSelectResultV1DTO, SingleSelectResultV1>(
        dto,
        "singleSelectNpsResultsBreakdownV1",
        SingleSelectResultV1.fromDTO,
        InvalidQuestionViewTypeV1SingleSelectNpsSingleSelectNpsResultsBreakdownV1Error
      );
      const singleSelectNpsGraphType = SingleSelectNpsGraphTypeV1.fromDTO(getObject(dto, "singleSelectNpsGraphType", InvalidQuestionViewTypeV1SingleSelectNpsSingleSelectNpsGraphTypeError));
      return new SingleSelectNps(
        sampleBreakdown,
        promoters,
        passives,
        detractors,
        singleSelectNpsResultsBreakdownV1,
        singleSelectNpsGraphType
      );
    }
  
    toDTO(): SingleSelectNpsDTO {
      const dto: SingleSelectNpsDTO = {
        case: "SINGLE_SELECT_NPS",
          sampleBreakdown: this.sampleBreakdown.toDTO(),
          promoters: this.promoters.toDTO(),
          passives: this.passives.toDTO(),
          detractors: this.detractors.toDTO(),
          singleSelectNpsResultsBreakdownV1: this.singleSelectNpsResultsBreakdownV1.map((e: SingleSelectResultV1) => { return e.toDTO() }),
          singleSelectNpsGraphType: SingleSelectNpsGraphTypeV1.toDTO(this.singleSelectNpsGraphType)
      };
      return dto;
    }
    }
  
  SingleSelectNps.prototype.toString = function toString(): string {
    return `QuestionViewTypeV1.SINGLE_SELECT_NPS(sampleBreakdown='${this.sampleBreakdown}',promoters='${this.promoters}',passives='${this.passives}',detractors='${this.detractors}',singleSelectNpsResultsBreakdownV1='${this.singleSelectNpsResultsBreakdownV1}',singleSelectNpsGraphType='${this.singleSelectNpsGraphType}')`;
  };
  
  export interface MultiSelectDTO {
    case: string;
    sampleBreakdown: SampleBreakdownDTO;
    multiSelectResults: MultiSelectResultV1DTO[];
    multiSelectGraphType: MultiSelectGraphTypeV1.MultiSelectGraphTypeV1DTO;
  }
  
  export class MultiSelect {
    readonly isQuestionViewTypeV1MultiSelect: boolean = true;
  
    readonly sampleBreakdown: SampleBreakdown;
    readonly multiSelectResults: MultiSelectResultV1[];
    readonly multiSelectGraphType: MultiSelectGraphTypeV1.MultiSelectGraphTypeV1;
  
  
    constructor(
    sampleBreakdown: SampleBreakdown, 
    multiSelectResults: MultiSelectResultV1[], 
    multiSelectGraphType: MultiSelectGraphTypeV1.MultiSelectGraphTypeV1
    ) {
      this.sampleBreakdown = sampleBreakdown;
      this.multiSelectResults = multiSelectResults;
      this.multiSelectGraphType = multiSelectGraphType;
    }
  
    static fromDTO(dto: MultiSelectDTO): MultiSelect {
      const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidQuestionViewTypeV1SingleSelectSampleBreakdownError));
      const multiSelectResults = getList<MultiSelectResultV1DTO, MultiSelectResultV1>(
        dto,
        "multiSelectResults",
        MultiSelectResultV1.fromDTO,
        InvalidQuestionViewTypeV1MultiSelectMultiSelectResultsError
      );
      const multiSelectGraphType = MultiSelectGraphTypeV1.fromDTO(getObject(dto, "multiSelectGraphType", InvalidQuestionViewTypeV1MultiSelectMultiSelectGraphTypeError));
      return new MultiSelect(
        sampleBreakdown,
        multiSelectResults,
        multiSelectGraphType
      );
    }
  
    toDTO(): MultiSelectDTO {
      const dto: MultiSelectDTO = {
        case: "MULTI_SELECT",
          sampleBreakdown: this.sampleBreakdown.toDTO(),
          multiSelectResults: this.multiSelectResults.map((e: MultiSelectResultV1) => { return e.toDTO() }),
          multiSelectGraphType: MultiSelectGraphTypeV1.toDTO(this.multiSelectGraphType)
      };
      return dto;
    }
    }
  
  MultiSelect.prototype.toString = function toString(): string {
    return `QuestionViewTypeV1.MULTI_SELECT(sampleBreakdown='${this.sampleBreakdown}',multiSelectResults='${this.multiSelectResults}',multiSelectGraphType='${this.multiSelectGraphType}')`;
  };
  
  export interface RankingDTO {
    case: string;
    sampleBreakdown: SampleBreakdownDTO;
    rankingResults: RankingResultV1DTO[];
    rankingGraphType: RankingGraphTypeV1.RankingGraphTypeV1DTO;
  }
  
  export class Ranking {
    readonly isQuestionViewTypeV1Ranking: boolean = true;
  
    readonly sampleBreakdown: SampleBreakdown;
    readonly rankingResults: RankingResultV1[];
    readonly rankingGraphType: RankingGraphTypeV1.RankingGraphTypeV1;
  
  
    constructor(
    sampleBreakdown: SampleBreakdown, 
    rankingResults: RankingResultV1[], 
    rankingGraphType: RankingGraphTypeV1.RankingGraphTypeV1
    ) {
      this.sampleBreakdown = sampleBreakdown;
      this.rankingResults = rankingResults;
      this.rankingGraphType = rankingGraphType;
    }
  
    static fromDTO(dto: RankingDTO): Ranking {
      const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidQuestionViewTypeV1SingleSelectSampleBreakdownError));
      const rankingResults = getList<RankingResultV1DTO, RankingResultV1>(
        dto,
        "rankingResults",
        RankingResultV1.fromDTO,
        InvalidQuestionViewTypeV1RankingRankingResultsError
      );
      const rankingGraphType = RankingGraphTypeV1.fromDTO(getObject(dto, "rankingGraphType", InvalidQuestionViewTypeV1RankingRankingGraphTypeError));
      return new Ranking(
        sampleBreakdown,
        rankingResults,
        rankingGraphType
      );
    }
  
    toDTO(): RankingDTO {
      const dto: RankingDTO = {
        case: "RANKING",
          sampleBreakdown: this.sampleBreakdown.toDTO(),
          rankingResults: this.rankingResults.map((e: RankingResultV1) => { return e.toDTO() }),
          rankingGraphType: RankingGraphTypeV1.toDTO(this.rankingGraphType)
      };
      return dto;
    }
    }
  
  Ranking.prototype.toString = function toString(): string {
    return `QuestionViewTypeV1.RANKING(sampleBreakdown='${this.sampleBreakdown}',rankingResults='${this.rankingResults}',rankingGraphType='${this.rankingGraphType}')`;
  };
  
  export interface OpenEndedDTO {
    case: string;
    sampleBreakdown: SampleBreakdownDTO;
    openEndedResults: OpenEndedFieldResultV1DTO[];
    openEndedGraphType: OpenEndedGraphTypeV1.OpenEndedGraphTypeV1DTO;
  }
  
  export class OpenEnded {
    readonly isQuestionViewTypeV1OpenEnded: boolean = true;
  
    readonly sampleBreakdown: SampleBreakdown;
    readonly openEndedResults: OpenEndedFieldResultV1[];
    readonly openEndedGraphType: OpenEndedGraphTypeV1.OpenEndedGraphTypeV1;
  
    static readonly constraints = {
      "openEndedResults": {
        "maxSize": 16,
      },
    }
  
    constructor(
    sampleBreakdown: SampleBreakdown, 
    openEndedResults: OpenEndedFieldResultV1[], 
    openEndedGraphType: OpenEndedGraphTypeV1.OpenEndedGraphTypeV1
    ) {
      this.sampleBreakdown = sampleBreakdown;
      OpenEnded.validateOpenEndedResults(openEndedResults);
      this.openEndedResults = openEndedResults;
      this.openEndedGraphType = openEndedGraphType;
    }
  
    static fromDTO(dto: OpenEndedDTO): OpenEnded {
      const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidQuestionViewTypeV1SingleSelectSampleBreakdownError));
      const openEndedResults = getList<OpenEndedFieldResultV1DTO, OpenEndedFieldResultV1>(
        dto,
        "openEndedResults",
        OpenEndedFieldResultV1.fromDTO,
        InvalidQuestionViewTypeV1OpenEndedOpenEndedResultsError
      );
      const openEndedGraphType = OpenEndedGraphTypeV1.fromDTO(getObject(dto, "openEndedGraphType", InvalidQuestionViewTypeV1OpenEndedOpenEndedGraphTypeError));
      return new OpenEnded(
        sampleBreakdown,
        openEndedResults,
        openEndedGraphType
      );
    }
  
    toDTO(): OpenEndedDTO {
      const dto: OpenEndedDTO = {
        case: "OPEN_ENDED",
          sampleBreakdown: this.sampleBreakdown.toDTO(),
          openEndedResults: this.openEndedResults.map((e: OpenEndedFieldResultV1) => { return e.toDTO() }),
          openEndedGraphType: OpenEndedGraphTypeV1.toDTO(this.openEndedGraphType)
      };
      return dto;
    }
    private static validateOpenEndedResults = (openEndedResults: OpenEndedFieldResultV1[]): void => {
      if(openEndedResults.length > 16) {
        throw new InvalidQuestionViewTypeV1OpenEndedOpenEndedResultsMaxSizeError(
          16,
          `Attribute openEndedResults is too long. Size is ${openEndedResults.length}. Max size is 16.`
        );
      }
    }
    
  }
  
  OpenEnded.prototype.toString = function toString(): string {
    return `QuestionViewTypeV1.OPEN_ENDED(sampleBreakdown='${this.sampleBreakdown}',openEndedResults='${this.openEndedResults}',openEndedGraphType='${this.openEndedGraphType}')`;
  };
  
  export interface GroupDTO {
    case: string;
    groupElementResults: GroupElementResultV1DTO[];
  }
  
  export class Group {
    readonly isQuestionViewTypeV1Group: boolean = true;
  
    readonly groupElementResults: GroupElementResultV1[];
  
  
    constructor(
    groupElementResults: GroupElementResultV1[]
    ) {
      this.groupElementResults = groupElementResults;
    }
  
    static fromDTO(dto: GroupDTO): Group {
      const groupElementResults = getList<GroupElementResultV1DTO, GroupElementResultV1>(
        dto,
        "groupElementResults",
        GroupElementResultV1.fromDTO,
        InvalidQuestionViewTypeV1GroupGroupElementResultsError
      );
      return new Group(
        groupElementResults
      );
    }
  
    toDTO(): GroupDTO {
      const dto: GroupDTO = {
        case: "GROUP",
          groupElementResults: this.groupElementResults.map((e: GroupElementResultV1) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Group.prototype.toString = function toString(): string {
    return `QuestionViewTypeV1.GROUP(groupElementResults='${this.groupElementResults}')`;
  };
  
  export interface GridDTO {
    case: string;
    sampleBreakdown: SampleBreakdownDTO;
    gridResults: GridResultV1DTO[];
  }
  
  export class Grid {
    readonly isQuestionViewTypeV1Grid: boolean = true;
  
    readonly sampleBreakdown: SampleBreakdown;
    readonly gridResults: GridResultV1[];
  
  
    constructor(
    sampleBreakdown: SampleBreakdown, 
    gridResults: GridResultV1[]
    ) {
      this.sampleBreakdown = sampleBreakdown;
      this.gridResults = gridResults;
    }
  
    static fromDTO(dto: GridDTO): Grid {
      const sampleBreakdown = SampleBreakdown.fromDTO(getObject(dto, "sampleBreakdown", InvalidQuestionViewTypeV1SingleSelectSampleBreakdownError));
      const gridResults = getList<GridResultV1DTO, GridResultV1>(
        dto,
        "gridResults",
        GridResultV1.fromDTO,
        InvalidQuestionViewTypeV1GridGridResultsError
      );
      return new Grid(
        sampleBreakdown,
        gridResults
      );
    }
  
    toDTO(): GridDTO {
      const dto: GridDTO = {
        case: "GRID",
          sampleBreakdown: this.sampleBreakdown.toDTO(),
          gridResults: this.gridResults.map((e: GridResultV1) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Grid.prototype.toString = function toString(): string {
    return `QuestionViewTypeV1.GRID(sampleBreakdown='${this.sampleBreakdown}',gridResults='${this.gridResults}')`;
  };
  

  export type QuestionViewTypeV1 = SingleSelect | SingleSelectWithProportion | SingleSelectNps | MultiSelect | Ranking | OpenEnded | Group | Grid;

  export type QuestionViewTypeV1DTO = SingleSelectDTO | SingleSelectWithProportionDTO | SingleSelectNpsDTO | MultiSelectDTO | RankingDTO | OpenEndedDTO | GroupDTO | GridDTO;

  export const fromDTO = (dto: QuestionViewTypeV1DTO): QuestionViewTypeV1 => {
    switch (dto.case) {
      case "SINGLE_SELECT":
          return SingleSelect.fromDTO(dto as SingleSelectDTO);
      case "SINGLE_SELECT_WITH_PROPORTION":
          return SingleSelectWithProportion.fromDTO(dto as SingleSelectWithProportionDTO);
      case "SINGLE_SELECT_NPS":
          return SingleSelectNps.fromDTO(dto as SingleSelectNpsDTO);
      case "MULTI_SELECT":
          return MultiSelect.fromDTO(dto as MultiSelectDTO);
      case "RANKING":
          return Ranking.fromDTO(dto as RankingDTO);
      case "OPEN_ENDED":
          return OpenEnded.fromDTO(dto as OpenEndedDTO);
      case "GROUP":
          return Group.fromDTO(dto as GroupDTO);
      case "GRID":
          return Grid.fromDTO(dto as GridDTO);
      default:
        throw new InvalidQuestionViewTypeV1Error(
            `Case ${dto.case} is not valid case of QuestionViewTypeV1.`
        );
    }
  };
}

export class InvalidQuestionViewTypeV1Error extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1Error";
  }
}
export class InvalidQuestionViewTypeV1SingleSelectError extends InvalidQuestionViewTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectSampleBreakdownError extends InvalidQuestionViewTypeV1SingleSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectSampleBreakdownError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectSingleSelectResultsError extends InvalidQuestionViewTypeV1SingleSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectSingleSelectResultsError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectSingleSelectGraphTypeError extends InvalidQuestionViewTypeV1SingleSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectSingleSelectGraphTypeError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectWithProportionError extends InvalidQuestionViewTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectWithProportionError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectWithProportionSampleBreakdownError extends InvalidQuestionViewTypeV1SingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectWithProportionSampleBreakdownError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectWithProportionSingleSelectResultsError extends InvalidQuestionViewTypeV1SingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectWithProportionSingleSelectResultsError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectWithProportionProportionChartTypeError extends InvalidQuestionViewTypeV1SingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectWithProportionProportionChartTypeError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectWithProportionSingleSelectWithProportionGraphTypeError extends InvalidQuestionViewTypeV1SingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectWithProportionSingleSelectWithProportionGraphTypeError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectWithProportionNaOptionCodeError extends InvalidQuestionViewTypeV1SingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectWithProportionNaOptionCodeError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectNpsError extends InvalidQuestionViewTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectNpsError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectNpsSampleBreakdownError extends InvalidQuestionViewTypeV1SingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectNpsSampleBreakdownError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectNpsPromotersError extends InvalidQuestionViewTypeV1SingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectNpsPromotersError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectNpsPassivesError extends InvalidQuestionViewTypeV1SingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectNpsPassivesError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectNpsDetractorsError extends InvalidQuestionViewTypeV1SingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectNpsDetractorsError";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectNpsSingleSelectNpsResultsBreakdownV1Error extends InvalidQuestionViewTypeV1SingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectNpsSingleSelectNpsResultsBreakdownV1Error";
  }
}

export class InvalidQuestionViewTypeV1SingleSelectNpsSingleSelectNpsGraphTypeError extends InvalidQuestionViewTypeV1SingleSelectNpsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1SingleSelectNpsSingleSelectNpsGraphTypeError";
  }
}

export class InvalidQuestionViewTypeV1MultiSelectError extends InvalidQuestionViewTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1MultiSelectError";
  }
}

export class InvalidQuestionViewTypeV1MultiSelectSampleBreakdownError extends InvalidQuestionViewTypeV1MultiSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1MultiSelectSampleBreakdownError";
  }
}

export class InvalidQuestionViewTypeV1MultiSelectMultiSelectResultsError extends InvalidQuestionViewTypeV1MultiSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1MultiSelectMultiSelectResultsError";
  }
}

export class InvalidQuestionViewTypeV1MultiSelectMultiSelectGraphTypeError extends InvalidQuestionViewTypeV1MultiSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1MultiSelectMultiSelectGraphTypeError";
  }
}

export class InvalidQuestionViewTypeV1RankingError extends InvalidQuestionViewTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1RankingError";
  }
}

export class InvalidQuestionViewTypeV1RankingSampleBreakdownError extends InvalidQuestionViewTypeV1RankingError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1RankingSampleBreakdownError";
  }
}

export class InvalidQuestionViewTypeV1RankingRankingResultsError extends InvalidQuestionViewTypeV1RankingError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1RankingRankingResultsError";
  }
}

export class InvalidQuestionViewTypeV1RankingRankingGraphTypeError extends InvalidQuestionViewTypeV1RankingError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1RankingRankingGraphTypeError";
  }
}

export class InvalidQuestionViewTypeV1OpenEndedError extends InvalidQuestionViewTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1OpenEndedError";
  }
}

export class InvalidQuestionViewTypeV1OpenEndedSampleBreakdownError extends InvalidQuestionViewTypeV1OpenEndedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1OpenEndedSampleBreakdownError";
  }
}

export class InvalidQuestionViewTypeV1OpenEndedOpenEndedResultsError extends InvalidQuestionViewTypeV1OpenEndedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1OpenEndedOpenEndedResultsError";
  }
}

export class InvalidQuestionViewTypeV1OpenEndedOpenEndedResultsMaxSizeError extends InvalidQuestionViewTypeV1OpenEndedOpenEndedResultsError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1OpenEndedOpenEndedResultsMaxSizeError";
  }
}

export class InvalidQuestionViewTypeV1OpenEndedOpenEndedGraphTypeError extends InvalidQuestionViewTypeV1OpenEndedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1OpenEndedOpenEndedGraphTypeError";
  }
}

export class InvalidQuestionViewTypeV1GroupError extends InvalidQuestionViewTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1GroupError";
  }
}

export class InvalidQuestionViewTypeV1GroupGroupElementResultsError extends InvalidQuestionViewTypeV1GroupError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1GroupGroupElementResultsError";
  }
}

export class InvalidQuestionViewTypeV1GridError extends InvalidQuestionViewTypeV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1GridError";
  }
}

export class InvalidQuestionViewTypeV1GridSampleBreakdownError extends InvalidQuestionViewTypeV1GridError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1GridSampleBreakdownError";
  }
}

export class InvalidQuestionViewTypeV1GridGridResultsError extends InvalidQuestionViewTypeV1GridError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidQuestionViewTypeV1GridGridResultsError";
  }
}
